<template>
  <div>
    <b-table
      class="manual-table"
      :items="manualTimes"
      :fields="columns"
      :busy="isLoading"
      show-empty
      striped
      responsive
    >
      <template #head()="data">
        <span class="text-capitalize">{{ data.label }}</span>
      </template>
      <template #cell(id)="data">
        {{ data.index + 1 }}
      </template>
      <template #cell(name)="data">
        <b-avatar
          :src="getAvatar(data.item.user.avatar)"
          size="30"
          badge
          :badge-variant="data.item.user.isOnline ? 'success' : 'secondary'"
          :text="data.item.user.name.charAt(0)"
          class="mr-1"
          variant="light-primary"
        />
        <span>{{ IS_MOBILE() ? FORMAT_NAME(data.item.user.name) : data.item.user.name }}</span>
      </template>
      <template #cell(date)="data">
        {{ moment(data.item.startedAt).format('MMM DD, YYYY') }}
      </template>
      <template #cell(startedAt)="data">
        {{ moment(data.item.startedAt).format('hh:mm A') }}
      </template>
      <template #cell(endedAt)="data">
        {{ moment(data.item.endedAt).format('hh:mm A') }}
      </template>
      <template #cell(remarks)="data">
        <span
          v-if="data.item.remarks"
          v-b-tooltip.hover.left="data.item.remarks"
        >
          {{ data.item.remarks }}
        </span>
      </template>
      <template #cell(duration)="data">
        <b-badge
          variant="primary"
          class="w-75"
        >
          {{ [getDifferenceInHours(data.item.startedAt, data.item.endedAt) && `${getDifferenceInHours(data.item.startedAt, data.item.endedAt)}h`, getDifferenceInMinutes(data.item.startedAt, data.item.endedAt) % 60 && `${getDifferenceInMinutes(data.item.startedAt, data.item.endedAt) % 60}m`].filter(Boolean).join(' ') }}
        </b-badge>
      </template>

      <!-- Actions -->
      <template #cell(actions)="data">
        <div class="d-flex">
          <b-button
            v-b-tooltip.hover="'Approve'"
            variant="primary"
            size="sm"
            class="action-btn"
            @click="updateManualTime(data.item.id, data.item.user.uuid, false)"
          >
            <feather-icon icon="CheckIcon" />
          </b-button>

          <b-button
            v-b-tooltip.hover="'Reject'"
            variant="danger"
            size="sm"
            class="ml-2 action-btn"
            @click="manualTimeToReject = data.item"
          >
            <feather-icon icon="XIcon" />
          </b-button>
        </div>
      </template>
    </b-table>

    <!-- Pagination -->
    <div class="w-100 centralize">
      <b-pagination
        v-if="total > first"
        v-model="page"
        :per-page="first"
        :total-rows="total"
        @input="getAllManualTimes"
      />
    </div>

    <!-- Confirm Reject Modal -->
    <confirm-modal
      :delete-params="{
        title: $t('Reject Manual Time'),
        body: $t('reports.reject-confirmation')
      }"
      :processing="isLoading"
      variant="danger"
      :visible="!!manualTimeToReject"
      @close="manualTimeToReject = null"
      @onConfirm="updateManualTime(manualTimeToReject.id, manualTimeToReject.user.uuid, true)"
    />
  </div>
</template>

<script setup>
import useApollo from '@/plugins/graphql/useApollo'
import {
  BTable, BButton, BAvatar, BPagination, BBadge, VBTooltip,
} from 'bootstrap-vue'
import {
  computed, getCurrentInstance, onMounted, ref, watch,
} from 'vue'
import ConfirmModal from '@/views/common/components/ConfirmModal.vue'
import store from '@/store'
import moment from 'moment'
import i18n from '@/libs/i18n'

defineExpose({
  directives: {
    'b-tooltip': VBTooltip,
  },
})

const props = defineProps({
  form: {
    type: Object,
    default: () => {},
  },
})

const instance = getCurrentInstance().proxy
const manualTimes = ref([])
const columns = ref([
  { key: 'id', label: i18n.t('S.No.'), class: 'w-2 px-1' },
  { key: 'name', label: i18n.t('User'), class: 'w-20 px-2' },
  { key: 'date', label: i18n.t('Date') },
  { key: 'startedAt', label: i18n.t('Start Time') },
  { key: 'endedAt', label: i18n.t('End Time') },
  { key: 'duration', label: i18n.t('Duration') },
  {
    key: 'remarks', label: i18n.t('Remarks'), tdClass: 'text-truncate--3', thStyle: 'border-bottom:0; width:20%',
  },
  { key: 'actions', label: i18n.t('Actions') },
])
const total = ref(0)
const first = 10
const page = ref(1)
const isLoading = ref(false)
const manualTimeToReject = ref(null)

const projectId = computed(() => store.state.project.selectedProject)

const getAllManualTimes = () => {
  isLoading.value = true
  useApollo.activities.getAllManualTime({
    projectId: projectId.value,
    first,
    page: page.value,
    startTime: instance.$root.timeWithOutTimeZone(props.form.start_date),
    endTime: instance.$root.timeWithOutTimeZone(props.form.end_date),
    timezone: props.form.time_zone,
  }).then(response => {
    total.value = response.data.project.pendingManualTimes.total
    manualTimes.value = response.data.project.pendingManualTimes.data
  }).finally(() => {
    isLoading.value = false
  })
}

const getDifferenceInHours = (start, end) => moment(end).diff(moment(start), 'hours')

const getDifferenceInMinutes = (start, end) => moment(end).diff(moment(start), 'minutes')

const updateManualTime = (recordId, userUid, decline) => {
  isLoading.value = true
  useApollo.activities.updatePendingManualTime({
    projectUid: projectId.value,
    userUid,
    record: Number(recordId),
    decline,
  }).then(response => {
    instance.$root.showSuccess(response.data?.approveManualTimeRecord?.message)
    getAllManualTimes()
  }).catch(error => {
    instance.$root.showError(error)
  }).finally(() => {
    isLoading.value = false
    manualTimeToReject.value = null
  })
}

watch(() => props.form, () => {
  getAllManualTimes()
}, { deep: true })

onMounted(() => {
  getAllManualTimes()
})

</script>

<style lang="scss">
.manual-table {
  width: 100%;
  table-layout: fixed;
  .action-btn {
    padding: 8px !important;
  }
  .w-2 {
    width: 2% !important;
  }
  .w-10 {
    width: 10% !important;
  }
  .w-20 {
    width: 20% !important;
  }
  .text-truncate--3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    padding: 4px 28px;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: normal;
  }
}
</style>
